import React, { useEffect, useState } from 'react';
import '../../App.css';
import './data-management.css';
import '../../tools/search-results/search-results.css';
import { useTranslation } from 'react-i18next';
import { useAppSelector, useAppDispatch } from '../../app/hooks';

// icons

// import responsive layout items.
import { Col, Row } from 'react-bootstrap';

// types
import {
  CanfarServiceType,
  DataItemType,
  FileBrowserPage,
  JobType,
  RucioLocationsType,
  Site,
} from '../../utils/types';
import {
  Event,
  EventType,
  EventTarget,
  InitiateDMSearchParams,
} from '../../utils/events';

// import styles.
import {
  customStylesDark,
  customStylesLight,
} from '../../utils/custom-styles-select-box';

// enum
import { SidePanelPosition } from '../../tools/side-panel';

// functions
import { ContainerHook, siteName } from '../../utils/functions';
import { handleKeyboardActivation } from '../../utils/accessibility';

// classes
import { CheckBox } from '../../tools/controls';
import DataManagementTable from './data-management-table';
import { DisplayFileSize } from '../../utils/functions';
import Scrollbox from '../../tools/scrollbox';
import SidePanel from '../../tools/side-panel';
import SiteIcon from '../../tools/site-icon';
import ComputeServicesPanel from '../panels/compute-services-panel';
import EditableSelect from '../../tools/editable-select';
import DataStagingComponent from './data-staging-component';
import { ContextMenu } from '../menus/context-menu';

//	--------------------------------------------------------------------------
//
//	R E D U X   S T A T E   M A N A G E M E N T
//
//	--------------------------------------------------------------------------

// import state definition and functions (redux).
import {
  setContextMenuRefresh,
  setParametersMaximised,
  setDetailsMaximised,
  setComputeMaximised,
  setFilenameFilter,
  setLifetimeStr,
  setLifetime,
  setShowHiddenFiles,
} from './file-browser-slice';
import { getRucioSites } from './file-browser-actions';
import { rG_setSite } from '../../app/global-slice';
import {
  fnD_buildRucioLocations,
  fnD_countAvailableFiles,
  fnD_getAvailableLocations,
  fnD_getStorageLocationSite,
} from '../../app/data-actions';
import {
  CONTEXT_SYSTEMS,
  CONTEXT_CONTAINERS,
  ctx_setContextRow,
  ctx_setContextVisible,
} from '../menus/context-menu-slice';
import { handleEvent } from '../../app-actions';

//	--------------------------------------------------------------------------
//
//	T Y P E S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	P R O P E R T I E S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	C L A S S   D E F I N I T I O N
//
//	--------------------------------------------------------------------------

export default function DataManagement(props: {
  dataManagementJobs: JobType[];
  jobsLoading: boolean;
  tabVisible: boolean;
}) {
  //
  //	--------------------------------------------------------------------------
  //
  //	R E D U X   S T A T E   H A N D L I N G
  //
  //	--------------------------------------------------------------------------

  const dispatch = useAppDispatch();

  // side panels maximised, or not?
  const sParametersMaximised: boolean = useAppSelector(
    (state) => state.fileBrowser.parametersMaximised,
  );
  const sDetailsMaximised: boolean = useAppSelector(
    (state) => state.fileBrowser.detailsMaximised,
  );
  const sComputeMaximised: boolean = useAppSelector(
    (state) => state.fileBrowser.computeMaximised,
  );

  const sContextMenuRefresh: number = useAppSelector(
    (state) => state.fileBrowser.contextMenuRefresh,
  );

  // global state parameters.
  const sG_selectedCanfar: CanfarServiceType | undefined = useAppSelector(
    (state) => state.global.selectedCanfar,
  );
  const sG_project: { id: number; name?: string } | undefined = useAppSelector(
    (state) => state.global.project,
  );
  const sG_username: string | undefined = useAppSelector(
    (state) => state.global.username,
  );
  const sG_preferredUsername: string | undefined = useAppSelector(
    (state) => state.global.preferredUsername,
  );
  const sG_darkMode: boolean = useAppSelector((state) => state.global.darkMode);
  const sG_site: Site | undefined = useAppSelector((state) => state.global.site);

  // data state parameters.
  const sD_sites: Site[] = useAppSelector((state) => state.data.sites);
  const sD_rucioLocations: RucioLocationsType[] = useAppSelector(
    (state) => state.data.rucioLocations,
  );

  // current page displayed.
  const sPageDisplayed: FileBrowserPage = useAppSelector(
    (state) => state.fileBrowser.pageParams.pageDisplayed,
  );

  // filter values.
  const sFilenameFilter: string[] = useAppSelector(
    (state) => state.fileBrowser.pageParams.filenameFilter,
  );
  const sFileTypeFilter: string = useAppSelector(
    (state) => state.fileBrowser.pageParams.fileTypeFilter,
  );
  const sShowHiddenFiles: boolean = useAppSelector(
    (state) => state.fileBrowser.pageParams.showHiddenFiles,
  );

  // lifetime.
  const sLifetimeStr: string = useAppSelector((state) => state.fileBrowser.lifetimeStr);

  // table state.
  const sNamespace: string | undefined = useAppSelector(
    (state) => state.fileBrowser.pageParams.namespace,
  );
  const sJobID: string | undefined = useAppSelector(
    (state) => state.fileBrowser.pageParams.jobID,
  );
  const sDataset: string | undefined = useAppSelector(
    (state) => state.fileBrowser.pageParams.dataset,
  );
  const sCollection: { id: number; name: string } | undefined = useAppSelector(
    (state) => state.fileBrowser.pageParams.collection,
  );

  // user and project storage paths.
  const sStoragePath: string[] = useAppSelector(
    (state) => state.fileBrowser.pageParams.storagePath,
  );

  // selected items.
  const sSelectedItems: DataItemType[] = useAppSelector(
    (state) => state.fileBrowser.selectedItems,
  );

  const sDisplayComputePanel: boolean = useAppSelector(
    (state) => state.fileBrowser.displayComputePanel,
  );

  //	--------------------------------------------------------------------------
  //
  //	D E C L A R A T I O N S
  //
  //	--------------------------------------------------------------------------

  // translation function
  const { t } = useTranslation();

  // create a list of site option, based upon the list of sites.
  const _siteOptions: {
    value: string;
    label: string;
    icon?: string;
  }[] = sD_sites.map((site) => ({
    value: site.site_uuid ?? '',
    label: siteName({ site: site }),
    icon: site.icon /*site.icon === undefined ? '' : site.icon,*/,
  }));

  // monitor this container, and if it changes size update the state.
  const dataWindow = ContainerHook({
    container: CONTEXT_CONTAINERS.FILE_BROWSER_WHOLE_WINDOW,
  });

  // retrieve the list of sites (in availability order) for the project
  // items and selected project items.
  const selectedItemSites: {
    site: Site;
    count: number;
  }[] = getRucioSites({
    allItems: [],
    selectedItems: sSelectedItems,
    catalog: 'file-browser',
  });

  //	--------------------------------------------------------------------------
  //
  //	F U N C T I O N S
  //
  //	--------------------------------------------------------------------------

  //	------------------------------------------------------------
  //
  //	handler for changes to the input boxes.
  //
  //	------------------------------------------------------------

  const inputHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputBox: HTMLInputElement = event.target;

    if (inputBox.name === 'lifetime') {
      dispatch(setLifetimeStr(inputBox.value));
    }
    if (inputBox.name === 'showHiddenFiles') {
      dispatch(setShowHiddenFiles(inputBox.checked));

      if (
        sPageDisplayed === FileBrowserPage.UserStorage ||
        sPageDisplayed === FileBrowserPage.ProjectStorage
      ) {
        // run the data-management search.
        const params: InitiateDMSearchParams = {
          filename: sFilenameFilter.length >= 2 ? sFilenameFilter[1] : '',
          fileType: sFileTypeFilter,
          preferredUsername: sG_preferredUsername,
          storagePath: sStoragePath,
          canfarService: sG_selectedCanfar,
          showHiddenFiles: inputBox.checked,
          pageDisplayed: sPageDisplayed,
        };
        const newEvent: Event = {
          eventTarget: EventTarget.DATA_MANAGEMENT_TABLE,
          eventType: EventType.INITIATE_DM_SEARCH,
          parameters: params,
        };
        dispatch(
          handleEvent({
            event: newEvent,
          }),
        );
      }
    }
  }; // inputHandler

  //	------------------------------------------------------------
  //
  //	Restrict input box entry to numbers only, plus . , + -
  //
  //	------------------------------------------------------------

  const numericKeysOnly = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      (event.charCode < 48 || event.charCode > 57) &&
      (event.charCode < 43 || event.charCode > 46)
    )
      event.preventDefault();
  }; // numericKeysOnly

  //	------------------------------------------------------------
  //
  //	Handler for lost-focus events on the input boxes.
  //
  //	------------------------------------------------------------

  const onBlurEvent = (event: React.FocusEvent<HTMLInputElement>) => {
    const inputBox: HTMLInputElement = event.target;

    if (inputBox.name === 'lifetime') {
      // params.
      let lifetimeNumeric: number = 0.0;
      try {
        lifetimeNumeric = Number(sLifetimeStr);
      } catch (e) {}
      dispatch(setLifetime(lifetimeNumeric));
    }
  }; // onBlurEvent

  //	------------------------------------------------------------
  //
  //	close the context menus when the user clicks anywhere
  //	in the container.
  //
  //	------------------------------------------------------------

  function onClickDivHandler(event: React.MouseEvent<HTMLDivElement>) {
    //
    dispatch(
      ctx_setContextRow({
        contextSystem: CONTEXT_SYSTEMS.FILE_BROWSER_STAGING,
        row: -1,
      }),
    );
    dispatch(
      ctx_setContextVisible({
        contextSystem: CONTEXT_SYSTEMS.FILE_BROWSER_STAGING,
        visible: false,
      }),
    );
    dispatch(setContextMenuRefresh(Date.now()));
    //
  } // onClickDivHandler

  //	------------------------------------------------------------
  //
  //	Handler for div clicks.
  //
  //	------------------------------------------------------------

  const handleNavigationSelection = (targetId: string) => {
    var namespace: string | undefined = undefined;
    var filename: string = '';
    var pageDisplayed: FileBrowserPage = FileBrowserPage.FileBrowser;
    var storagePath: string[] = [];
    let cacheLevel: number = -1;

    if (targetId === 'dataManagement') {
      cacheLevel = 0;
    }
    if (targetId === 'namespaces') {
      pageDisplayed = FileBrowserPage.Namespaces;
      cacheLevel = 1;
    }
    if (targetId === 'collections') {
      pageDisplayed = FileBrowserPage.Collections;
      cacheLevel = 1;
    }
    if (targetId === 'items') {
      namespace = sNamespace;
      pageDisplayed = FileBrowserPage.Items;
      cacheLevel = 2;
    }

    // if the user has clicked on a directory path, then update the path
    // accordingly.
    let storagePathChanged: boolean = false;
    if (targetId.startsWith('userStorage_')) {
      const levelStr: string = targetId.substring('userStorage_'.length);
      let level: number = -1;
      try {
        level = Number(levelStr);
      } catch (e) {}

      storagePath = sStoragePath.slice();
      if (level > -1) {
        storagePath.splice(level, storagePath.length - level);
      }

      cacheLevel = 1 + level;

      storagePathChanged = true;
      pageDisplayed = FileBrowserPage.UserStorage;
    }

    if (targetId.startsWith('projectStorage_')) {
      const levelStr: string = targetId.substring('projectStorage_'.length);
      let level: number = -1;
      try {
        level = Number(levelStr);
      } catch (e) {}

      storagePath = sStoragePath.slice();
      if (level > -1) {
        storagePath.splice(level, storagePath.length - level);
      }

      cacheLevel = 1 + level;

      storagePathChanged = true;
      pageDisplayed = FileBrowserPage.ProjectStorage;
    }

    // remove part of the cache.
    if (cacheLevel > -1) {
      let filterCache: string[] = sFilenameFilter.slice();
      filterCache.splice(cacheLevel + 1, filterCache.length - cacheLevel);
      dispatch(setFilenameFilter(filterCache));
      filename = filterCache[cacheLevel];
    }

    if (
      targetId === 'dataManagement' ||
      targetId === 'namespaces' ||
      targetId === 'collections' ||
      targetId === 'items' ||
      storagePathChanged === true
    ) {
      // run the data-management search.
      // FIXME: Need a dynamic way to switch between user and project items
      const params: InitiateDMSearchParams = {
        namespace: namespace,
        project: sG_project,
        filename: filename,
        preferredUsername: sG_preferredUsername,
        storagePath: storagePath,
        canfarService: sG_selectedCanfar,
        showHiddenFiles: sShowHiddenFiles,
        fileType: sPageDisplayed === FileBrowserPage.Namespaces ? 'all' : sFileTypeFilter,
        pageDisplayed: pageDisplayed,
        storageType: pageDisplayed === FileBrowserPage.UserStorage ? 'user' : 'project',
        focus: true,
      };
      const newEvent: Event = {
        eventTarget: EventTarget.DATA_MANAGEMENT_TABLE,
        eventType: EventType.INITIATE_DM_SEARCH,
        parameters: params,
      };
      dispatch(
        handleEvent({
          event: newEvent,
        }),
      );
    }
    //
  }; // handleNavigationSelection

  //	--------------------------------------------------------------------------
  //
  //	C O M P O N E N T S
  //
  //	--------------------------------------------------------------------------

  //	------------------------------------------------------------
  //
  //	C O M P O N E N T   C O D E
  //
  //	------------------------------------------------------------

  return (
    <div
      /*sm={12}*/
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
      }}
    >
      <div
        style={{
          padding: '0px',
          margin: '0px 0px 5px 0px',
          alignItems: 'center',
          height: 'auto',
          display: 'flex',
          flexDirection: 'row',
        }}
      >
        <div
          role="navigation"
          aria-label="Navigate the file browser"
          className="text-colour"
          style={{
            flex: '1 1',
            height: 'auto',
            margin: '0px 0px 0px 0px',
            padding: '0px 0px 0px 10px',
            display: 'flex',
            flexDirection: 'row',
          }}
        >
          <NavigationNode
            text={t('File browser')}
            id="dataManagement"
            visible={true}
            clickable={sPageDisplayed !== FileBrowserPage.FileBrowser}
            onActivate={handleNavigationSelection}
            hideSeparator={true}
          />
          <NavigationNode
            text={t('Namespaces')}
            id="namespaces"
            visible={
              sPageDisplayed === FileBrowserPage.Namespaces ||
              sPageDisplayed === FileBrowserPage.Items ||
              sPageDisplayed === FileBrowserPage.Dataset
            }
            clickable={sPageDisplayed !== FileBrowserPage.Namespaces}
            onActivate={handleNavigationSelection}
          />
          <NavigationNode
            text={t('Collections')}
            id="collections"
            visible={
              sPageDisplayed === FileBrowserPage.Collections ||
              sPageDisplayed === FileBrowserPage.Collection
            }
            clickable={sPageDisplayed !== FileBrowserPage.Collections}
            onActivate={handleNavigationSelection}
          />
          <NavigationNode
            text={
              t('Namespace').toUpperCase() +
              ': ' +
              (sNamespace !== undefined ? sNamespace.toUpperCase() : '')
            }
            id="items"
            visible={
              sPageDisplayed === FileBrowserPage.Items ||
              sPageDisplayed === FileBrowserPage.Dataset
            }
            clickable={sPageDisplayed !== FileBrowserPage.Items}
            onActivate={handleNavigationSelection}
          />
          <NavigationNode
            text={
              t('Dataset').toUpperCase() +
              ': ' +
              (sDataset !== undefined ? sDataset.toUpperCase() : '')
            }
            id="dataset"
            visible={sPageDisplayed === FileBrowserPage.Dataset}
            clickable={sPageDisplayed !== FileBrowserPage.Dataset}
            onActivate={handleNavigationSelection}
          />
          <NavigationNode
            text={
              t('Job Details').toUpperCase() +
              ': ' +
              (sJobID !== undefined ? sJobID.toUpperCase() : '')
            }
            id="jobDetails"
            visible={sPageDisplayed === FileBrowserPage.JobDetails}
            clickable={sPageDisplayed !== FileBrowserPage.JobDetails}
            onActivate={handleNavigationSelection}
          />
          <NavigationNode
            text={
              t('Collection').toUpperCase() +
              ': ' +
              (sCollection !== undefined ? sCollection.name.toUpperCase() : '')
            }
            id="collection"
            visible={sPageDisplayed === FileBrowserPage.Collection}
            clickable={sPageDisplayed !== FileBrowserPage.Collection}
            onActivate={handleNavigationSelection}
          />
          <NavigationNode
            text={sG_username === undefined ? 'HOME' : sG_username.toUpperCase()}
            id="userStorage_0"
            visible={sPageDisplayed === FileBrowserPage.UserStorage}
            clickable={sStoragePath.length > 0}
            onActivate={handleNavigationSelection}
          />
          {sStoragePath.map((item, index) => (
            <NavigationNode
              text={item.toUpperCase()}
              id={'userStorage_' + (index + 1).toString()}
              visible={sPageDisplayed === FileBrowserPage.UserStorage}
              clickable={sStoragePath.length > index + 1}
              onActivate={handleNavigationSelection}
            />
          ))}
          <NavigationNode
            text={`Project (${sG_project?.name === undefined ? 'HOME' : sG_project?.name.toUpperCase()})`}
            id="projectStorage_0"
            visible={sPageDisplayed === FileBrowserPage.ProjectStorage}
            clickable={sStoragePath.length > 0}
            onActivate={handleNavigationSelection}
          />
          {sStoragePath.map((item, index) => (
            <NavigationNode
              text={item.toUpperCase()}
              id={'projectStorage_' + (index + 1).toString()}
              visible={sPageDisplayed === FileBrowserPage.ProjectStorage}
              clickable={sStoragePath.length > index + 1}
              onActivate={handleNavigationSelection}
            />
          ))}
          <div style={{ flex: '1 1' }} />
        </div>
        <div
          style={{
            flex: '0 0 auto',
            display: 'flex',
            margin: '0px 10px 0px 0px',
          }}
        >
          <label id="file-browser-site" className="text-colour">
            Site (cavern):
          </label>
        </div>
        <div
          style={{
            flex: '0 0 200px',
            margin: '0px 5px 0px 0px',
          }}
        >
          <EditableSelect
            aria-labelledby="file-browser-site"
            gatewayTheme={sG_darkMode ? 'dark' : 'light'}
            styles={sG_darkMode ? customStylesDark : customStylesLight}
            isDisabled={
              sPageDisplayed !== FileBrowserPage.UserStorage &&
              sPageDisplayed !== FileBrowserPage.ProjectStorage &&
              sPageDisplayed !== FileBrowserPage.FileBrowser
            }
            isSearchable={false}
            options={_siteOptions}
            placeholder={t('Site')}
            value={_siteOptions.find((site) => site.value === sG_site?.site_uuid)}
            onChange={(
              selectedOption: {
                value: string | number;
                label: string;
                icon?: string;
              } | null,
            ) =>
              dispatch(
                rG_setSite(
                  sD_sites.find((find) => find.site_uuid === selectedOption?.value) ?? {},
                ),
              )
            }
            isClearable={false}
            showSiteIcon={true}
          />
        </div>
      </div>

      <div
        ref={dataWindow}
        id="file-browser-whole-window"
        style={{
          flex: '1 1',
          display: 'flex',
          flexDirection: 'row',
          padding: '0px 0px 0px 0px',
          margin: '0px 0px 0px 0px',
          width: '100%',
          position: 'relative',
        }}
        onClick={onClickDivHandler}
      >
        {/*
        /// main file browser section.
        */}
        <div
          style={{
            flex: '1 1',
            margin: '0px 10px 0px 0px',
            padding: '0px 0px 0px 0px',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <DataManagementTable />
          {((sDisplayComputePanel === true &&
            [
              FileBrowserPage.Items,
              FileBrowserPage.Dataset,
              FileBrowserPage.JobDetails,
              FileBrowserPage.Collection,
            ].indexOf(sPageDisplayed) > -1) ||
            sPageDisplayed === FileBrowserPage.UserStorage ||
            sPageDisplayed === FileBrowserPage.ProjectStorage) && (
            <div
              style={{
                flex: '0 0 auto',
                display: 'flex',
                flexDirection: 'row',
                margin: '0px 0px 0px 0px',
                padding: '0px',
              }}
            >
              <ComputeServicesPanel
                sites={selectedItemSites.length > 0 ? [selectedItemSites[0].site] : []}
                maximised={
                  sComputeMaximised === true &&
                  selectedItemSites.length > 0 &&
                  ((sDisplayComputePanel === true &&
                    [
                      FileBrowserPage.Items,
                      FileBrowserPage.Dataset,
                      FileBrowserPage.JobDetails,
                      FileBrowserPage.Collection,
                    ].indexOf(sPageDisplayed) > -1) ||
                    sPageDisplayed === FileBrowserPage.UserStorage ||
                    sPageDisplayed === FileBrowserPage.ProjectStorage)
                }
                minimised={
                  sComputeMaximised === false &&
                  selectedItemSites.length > 0 &&
                  ((sDisplayComputePanel === true &&
                    [
                      FileBrowserPage.Items,
                      FileBrowserPage.Dataset,
                      FileBrowserPage.JobDetails,
                      FileBrowserPage.Collection,
                    ].indexOf(sPageDisplayed) > -1) ||
                    sPageDisplayed === FileBrowserPage.UserStorage ||
                    sPageDisplayed === FileBrowserPage.ProjectStorage)
                }
                setComputeMaximised={setComputeMaximised}
              />
            </div>
          )}
        </div>

        <div
          style={{
            flex: '0 0',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            minHeight: 0,
            padding: '0px 0px 0px 0px',
            margin: '0px 5px 0px 0px',
          }}
        >
          <SidePanel
            position={SidePanelPosition.RIGHT}
            title={t('Details')}
            maximised={sDetailsMaximised === true}
            minimised={sDetailsMaximised === false}
            redux={true}
            setMaximised={setDetailsMaximised}
            width={'309px'}
            flex={sDetailsMaximised === true ? '1 1' : '0 0'}
          >
            <DetailsPane
              getLocations={
                sPageDisplayed === FileBrowserPage.Items ||
                sPageDisplayed === FileBrowserPage.Dataset ||
                sPageDisplayed === FileBrowserPage.JobDetails ||
                sPageDisplayed === FileBrowserPage.Collection
              }
            />
          </SidePanel>
          <div style={{ flex: '0 0 10px' }} />

          <SidePanel
            position={SidePanelPosition.RIGHT}
            title={t('Parameters')}
            maximised={sParametersMaximised}
            redux={true}
            setMaximised={setParametersMaximised}
            width={'309px'}
          >
            {/*
             //
             //	staging seconds parameter box
             //
             */}
            <label
              id="file-browser-lifetime-label"
              className="text-colour"
              style={{ margin: '0px 0px 0px 10px' }}
            >
              Staging lifetime [s]:
            </label>
            <div style={{ flex: '0 0 5px' }} />
            <div className="padding" data-padding="10px" style={{ alignItems: 'center' }}>
              <input
                className="input-field"
                aria-labelledby="file-browser-lifetime-label"
                type="text"
                name="lifetime"
                onKeyPress={numericKeysOnly}
                placeholder={t('Lifetime [s]')}
                onChange={inputHandler}
                onBlur={onBlurEvent}
                maxLength={50}
                value={sLifetimeStr}
                disabled={
                  sPageDisplayed === FileBrowserPage.UserStorage ||
                  sPageDisplayed === FileBrowserPage.ProjectStorage
                }
                data-disabled={
                  sPageDisplayed === FileBrowserPage.UserStorage ||
                  sPageDisplayed === FileBrowserPage.ProjectStorage
                    ? 'T'
                    : 'F'
                }
              />
            </div>
            <div style={{ flex: '0 0 5px' }} />
            <CheckBox
              name="showHiddenFiles"
              text={t('Show hidden files')}
              value={t('Show hidden files')}
              checked={sShowHiddenFiles === true}
              onChange={inputHandler}
              margin={15}
              enabled={
                sPageDisplayed === FileBrowserPage.UserStorage ||
                sPageDisplayed === FileBrowserPage.ProjectStorage
              }
            />
          </SidePanel>
        </div>
        <ContextMenu
          key={sContextMenuRefresh.toString()}
          system={CONTEXT_SYSTEMS.FILE_BROWSER_STAGING}
        />
      </div>
    </div>
  );
} // DataManagement

//	--------------------------------------------------------------------------
//
//	S U B   C O M P O N E N T S
//
//	--------------------------------------------------------------------------

//	------------------------------------------------------------
//
//	Displays details of the selected file(s)
//
//	------------------------------------------------------------

function DetailsPane(props: { getLocations: boolean }) {
  //
  //	--------------------------------------------------------------------------
  //
  //	R E D U X   S T A T E   H A N D L I N G
  //
  //	--------------------------------------------------------------------------

  const dispatch = useAppDispatch();

  // page currently displayed.
  const sPageDisplayed: FileBrowserPage = useAppSelector(
    (state) => state.fileBrowser.pageParams.pageDisplayed,
  );

  // the data items displayed in the table.
  const sAllItems: DataItemType[] = useAppSelector((state) => state.fileBrowser.dataList);

  // selected items.
  const sSelectedItems: DataItemType[] = useAppSelector(
    (state) => state.fileBrowser.selectedItems,
  );
  const sD_rucioLocations: RucioLocationsType[] = useAppSelector(
    (state) => state.data.rucioLocations,
  );

  //	--------------------------------------------------------------------------
  //
  //	D E C L A R A T I O N S
  //
  //	--------------------------------------------------------------------------

  const [sRetrievingLocations, setRetrievingLocations] = useState<boolean>(false);

  // add up the files.
  var totalSize: number = 0.0;
  for (var i = 0; i < sSelectedItems.length; i++) {
    if ((sSelectedItems[i].bytes ?? -1) >= 0)
      totalSize = totalSize + (sSelectedItems[i].bytes ?? 0);
  }

  //	--------------------------------------------------------------------------
  //
  //	H O O K S
  //
  //	--------------------------------------------------------------------------

  //	------------------------------------------------------------
  //
  //	get storage locations for the selected data item.
  //
  //	------------------------------------------------------------

  useEffect(() => {
    //
    // get a list of location where this data item is stored.
    if (sSelectedItems.length === 1) {
      const isDataItem = ['FILE', 'DATASET', 'CONTAINER'].includes(
        sSelectedItems[0].did_type?.toUpperCase() ?? '',
      );

      if (isDataItem && props.getLocations) {
        //
        fnD_buildRucioLocations({
          items: sSelectedItems,
          catalog: 'file-browser',
          refresh: false,
        });
        //
      }
    }
    //
  }, [sSelectedItems]);

  //	------------------------------------------------------------
  //
  //	C O M P O N E N T   C O D E
  //
  //	------------------------------------------------------------

  var singleFileSelected: boolean = false;
  if (sSelectedItems.length === 1) {
    if (
      sSelectedItems[0].did_type?.toUpperCase() === 'FILE' ||
      sSelectedItems[0].did_type?.toUpperCase() === 'DATASET' ||
      sSelectedItems[0].did_type?.toUpperCase() === 'CONTAINER'
    ) {
      singleFileSelected = true;
    }
  }

  return (
    <div
      style={{
        flex: '1 1',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'end',
        width: '100%',
      }}
    >
      <div
        className="text-colour"
        style={{
          flex: '0 0 auto',
          margin: '15px 10px 0px 0px',
          fontSize: '1.2em',
        }}
      >
        {sSelectedItems.length === 1
          ? sSelectedItems[0].filename
          : (sSelectedItems.length?.toString() ?? 0) + ' items selected'}
      </div>
      <div
        className="text-colour"
        style={{
          margin: '5px 10px 0px 15px',
        }}
      >
        Size: {DisplayFileSize({ bytes: totalSize })}
      </div>
      <div style={{ flex: '0 0 20px' }} />
      {sPageDisplayed === FileBrowserPage.Items && sSelectedItems.length > 0 && (
        <DataStagingComponent
          allItems={sAllItems}
          selectedItems={sSelectedItems}
          catalog="file-browser"
          contextSystem={CONTEXT_SYSTEMS.FILE_BROWSER_STAGING}
          contextMenuRefresh={setContextMenuRefresh}
          hideItemCount={true}
        />
      )}
    </div>
  );
} // DetailsPane

//	------------------------------------------------------------
//
//	Displays a clickable navigation node so the user can
//	navigate the file browser.
//
//	------------------------------------------------------------

function NavigationNode(props: {
  text: string;
  id: string;
  visible: boolean;
  clickable: boolean;
  onActivate: (id: string) => void;
  hideSeparator?: boolean;
  disabled?: boolean;
}) {
  //
  //	--------------------------------------------------------------------------
  //
  //	R E D U X   S T A T E   H A N D L I N G
  //
  //	--------------------------------------------------------------------------

  const sLoadingComponent: boolean = useAppSelector(
    (state) => state.fileBrowser.loadingComponent,
  );

  //	------------------------------------------------------------
  //
  //	C O M P O N E N T   C O D E
  //
  //	------------------------------------------------------------

  if (props.visible !== true) {
    return <></>;
  }

  const separator =
    props.hideSeparator !== true ? (
      <div
        style={{
          flex: '0 0 auto',
          margin: '0px 5px 0px 5px',
          display: 'flex',
          alignItems: 'center',
          userSelect: 'none',
        }}
        className="navigation"
      >
        &gt;
      </div>
    ) : (
      <></>
    );

  const baseStyle: React.CSSProperties = {
    flex: '0 0 auto',
    height: 'auto',
    padding: '0px 10px 0px 10px',
    userSelect: 'none',
  };

  const content = props.text.toUpperCase();

  if (props.clickable === true) {
    return (
      <>
        {separator}
        <button
          type="button"
          id={props.id}
          className="navigation"
          data-clickable="T"
          data-disabled={sLoadingComponent ? 'T' : 'F'}
          onClick={() => props.onActivate(props.id)}
          onKeyDown={(event) =>
            handleKeyboardActivation(event, () => props.onActivate(props.id))
          }
          style={{ ...baseStyle, background: 'none', border: 'none', cursor: 'pointer' }}
          disabled={sLoadingComponent}
        >
          {content}
        </button>
      </>
    );
  }

  return (
    <>
      {separator}
      <div
        id={props.id}
        className="navigation"
        data-clickable="F"
        style={baseStyle}
        aria-current="page"
      >
        {content}
      </div>
    </>
  );
} // NavigationNode
